// src/pages/TestOrders/sections/BillingAndInsuranceSection.jsx
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Heading,
    Input,
    SimpleGrid,
} from '@chakra-ui/react';
import Select from 'react-select';

const insuranceOrderOptions = [
    { value: 'primary', label: 'Primary Insurance' },
    { value: 'secondary', label: 'Secondary Insurance' },
    { value: 'tertiary', label: 'Tertiary Insurance' },
];

const BillingAndInsuranceSection = ({ formData, setFormData }) => {
    const insurances = formData.insurances || [
        { provider: '', memberId: '', order: null, sendLink: false, frontFile: null, backFile: null },
    ];
    const usedOrders = insurances.map(i => i.order?.value).filter(Boolean);

    const handleBillingChange = (opt) => {
        setFormData({ ...formData, billTo: opt?.value || '' });
    };

    const handleInsuranceChange = (idx, field, value) => {
        const updated = [...insurances];
        updated[idx][field] = value;
        setFormData({ ...formData, insurances: updated });
    };

    const addInsurance = () => {
        if (insurances.length < 3) {
            setFormData({
                ...formData,
                insurances: [
                    ...insurances,
                    { provider: '', memberId: '', order: null, sendLink: false, frontFile: null, backFile: null },
                ],
            });
        }
    };

    return (
        <Box p={4} borderWidth="1px" rounded="md" shadow="sm">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isInvalid={!formData.billTo}>
                    <FormLabel>Bill To</FormLabel>
                    <Select
                        options={[
                            { value: 'patient', label: 'Patient' },
                            { value: 'client', label: 'Client' },
                            { value: 'insurance', label: 'Insurance' },
                            { value: 'medicare', label: 'Medicare' },
                            { value: 'medi-cal', label: 'Medi-Cal' },
                        ]}
                        value={formData.billTo ? { value: formData.billTo, label: formData.billTo } : null}
                        onChange={handleBillingChange}
                        placeholder="Select Bill To"
                    />
                    {!formData.billTo && <FormErrorMessage>Bill To is required</FormErrorMessage>}
                </FormControl>

                <FormControl>
                    <FormLabel>Billing Note</FormLabel>
                    <Input
                        name="billingNote"
                        value={formData.billingNote || ''}
                        onChange={(e) => setFormData({ ...formData, billingNote: e.target.value })}
                    />
                </FormControl>
            </SimpleGrid>

            {/* Insurance details */}
            {['insurance', 'medicare', 'medi-cal'].includes(formData.billTo) && (
                <Box mt={4}>
                    <HStack justify="space-between" mb={3}>
                        <Checkbox
                            isChecked={formData.noInsurance || false}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setFormData({
                                    ...formData,
                                    noInsurance: checked,
                                    insurances: checked
                                        ? []
                                        : [{ provider: '', memberId: '', order: null, sendLink: false, frontFile: null, backFile: null }],
                                });
                            }}
                        >
                            This patient doesn’t have insurance
                        </Checkbox>

                        {insurances.length < 3 && !formData.noInsurance && (
                            <Button size="xs" colorScheme="blue" onClick={addInsurance}>
                                Add Insurance
                            </Button>
                        )}
                    </HStack>

                    {!formData.noInsurance &&
                        insurances.map((ins, idx) => (
                            <Box key={idx} p={3} borderWidth="1px" rounded="md" mb={4}>
                                {insurances.length > 1 && (
                                    <HStack justify="space-between" mb={3}>
                                        <Heading size="sm">Insurance {idx + 1}</Heading>
                                        <Button
                                            size="xs"
                                            colorScheme="red"
                                            variant="outline"
                                            onClick={() =>
                                                setFormData({
                                                    ...formData,
                                                    insurances: insurances.filter((_, i) => i !== idx),
                                                })
                                            }
                                        >
                                            Remove
                                        </Button>
                                    </HStack>
                                )}

                                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
                                    <FormControl>
                                        <FormLabel>Provider</FormLabel>
                                        <Input
                                            value={ins.provider}
                                            onChange={(e) => handleInsuranceChange(idx, 'provider', e.target.value)}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Member ID</FormLabel>
                                        <Input
                                            value={ins.memberId}
                                            onChange={(e) => handleInsuranceChange(idx, 'memberId', e.target.value)}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Order</FormLabel>
                                        <Select
                                            options={insuranceOrderOptions.filter(
                                                (opt) => ins.order?.value === opt.value || !usedOrders.includes(opt.value)
                                            )}
                                            value={ins.order}
                                            onChange={(opt) => handleInsuranceChange(idx, 'order', opt)}
                                            placeholder="Select Order"
                                        />
                                    </FormControl>
                                </SimpleGrid>

                                <Checkbox
                                    isChecked={ins.sendLink}
                                    onChange={(e) => handleInsuranceChange(idx, 'sendLink', e.target.checked)}
                                    mb={3}
                                >
                                    Send link to patient to upload insurance images
                                </Checkbox>

                                {!ins.sendLink && (
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                        <FormControl>
                                            <FormLabel>Insurance Front</FormLabel>
                                            <Input type="file" onChange={(e) => handleInsuranceChange(idx, 'frontFile', e.target.files[0])} />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel>Insurance Back</FormLabel>
                                            <Input type="file" onChange={(e) => handleInsuranceChange(idx, 'backFile', e.target.files[0])} />
                                        </FormControl>
                                    </SimpleGrid>
                                )}
                            </Box>
                        ))}
                </Box>
            )}
        </Box>
    );
};

export default BillingAndInsuranceSection;
